/**
 * Bubble diagram editor — pick a 2–7 bubble preset and edit each bubble's
 * title / body / tooltip. Writes through the effective override path so
 * default-mode clients edit the shared program template.
 */

import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import {
  BUBBLE_COUNTS,
  emptyBubblesForCount,
  resizeDiagramBubbles,
  type BubbleCount,
  type DiagramBubbleContent,
  type DiagramOverride,
} from "@/features/workshop/diagram-presets";
import { HAW_MODES } from "@/features/workshop/haw-content";
import { WORKSHOP_PANEL_DATA } from "@/features/missionControl/workshop-panel-data";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { Field, SectionFrame, Select, TextArea, TextInput } from "../editor-kit";
import { sameJson } from "../editor-utils";

function authoredDiagram(wsId: Workshop["id"]): DiagramOverride {
  if (wsId === "WS4") {
    const bubbles: DiagramBubbleContent[] = HAW_MODES.map((m) => ({
      id: m.id,
      titleLines: [m.title.toUpperCase()],
      bodyLines: m.subtitle ? [m.subtitle] : [],
      tipTitle: m.title,
      tipBody: m.body,
      subtitle: m.subtitle,
      badge: m.badge,
      lucide: m.lucide,
    }));
    return { bubbleCount: 5, bubbles };
  }
  const panel = WORKSHOP_PANEL_DATA[wsId];
  const count = Math.min(
    7,
    Math.max(2, panel.bubbles.length),
  ) as BubbleCount;
  const bubbles: DiagramBubbleContent[] = panel.bubbles.map((b) => ({
    id: b.id,
    titleLines: [...b.titleLines],
    bodyLines: [...b.bodyLines],
    tipTitle: b.tipTitle,
    tipBody: Array.isArray(b.tipBody) ? b.tipBody.join("\n") : b.tipBody,
  }));
  // Pad/truncate to a valid preset count.
  return {
    bubbleCount: count,
    bubbles: resizeDiagramBubbles(bubbles, count),
  };
}

function seedDraft(
  override: ReturnType<typeof useEffectiveWorkshopOverride>,
  wsId: Workshop["id"],
): DiagramOverride {
  return override?.diagram ?? authoredDiagram(wsId);
}

export function DiagramEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const current = seedDraft(override, wsId);
  const [draft, setDraft] = useState<DiagramOverride>(current);

  const setCount = (raw: string) => {
    const count = Number(raw) as BubbleCount;
    if (!BUBBLE_COUNTS.includes(count)) return;
    setDraft((d) => ({
      bubbleCount: count,
      bubbles:
        d.bubbles.length === count
          ? d.bubbles
          : d.bubbles.length === 0
            ? emptyBubblesForCount(count)
            : resizeDiagramBubbles(d.bubbles, count),
    }));
  };

  const setBubble = (i: number, patch: Partial<DiagramBubbleContent>) =>
    setDraft((d) => ({
      ...d,
      bubbles: d.bubbles.map((b, j) => (j === i ? { ...b, ...patch } : b)),
    }));

  const linesToText = (lines: string[]) => lines.join("\n");
  const textToLines = (text: string) =>
    text
      .split("\n")
      .map((l) => l.trimEnd())
      .filter((l, i, arr) => l.length > 0 || i < arr.length - 1);

  return (
    <SectionFrame
      description="Bubble diagram shown on Mission Control and the workshop dashboard. Pick a layout preset (2–7 bubbles), then edit each bubble’s copy."
      overridden={override?.diagram !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() => setEffectiveOverride(wsId, { diagram: draft })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "diagram");
        setDraft(authoredDiagram(wsId));
      }}
    >
      <Field label="Layout preset" hint="Changes the number of bubbles and their positions.">
        <Select
          value={String(draft.bubbleCount)}
          onChange={setCount}
          options={BUBBLE_COUNTS.map((n) => ({
            value: String(n),
            label: `${n} bubbles`,
          }))}
        />
      </Field>

      {draft.bubbles.map((b, i) => (
        <div
          key={b.id || i}
          className="flex flex-col gap-2 rounded-md border p-3"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <p className="font-mono text-fs-micro tracking-[0.18em] text-[color:var(--text-muted)] uppercase">
            Bubble {String(i + 1).padStart(2, "0")}
            {draft.bubbleCount >= 5 &&
            i === draft.bubbles.length - 1 &&
            (draft.bubbleCount === 5 || draft.bubbleCount === 7)
              ? " · Centre"
              : ""}
          </p>
          <Field label="Title lines">
            <TextArea
              value={linesToText(b.titleLines)}
              onChange={(e) =>
                setBubble(i, { titleLines: textToLines(e.target.value) })
              }
              rows={2}
            />
          </Field>
          <Field label="Body lines">
            <TextArea
              value={linesToText(b.bodyLines)}
              onChange={(e) =>
                setBubble(i, { bodyLines: textToLines(e.target.value) })
              }
              rows={2}
            />
          </Field>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Tooltip title">
              <TextInput
                value={b.tipTitle ?? ""}
                onChange={(e) => setBubble(i, { tipTitle: e.target.value })}
              />
            </Field>
            <Field label="Badge (optional)">
              <TextInput
                value={b.badge ?? ""}
                onChange={(e) => setBubble(i, { badge: e.target.value })}
              />
            </Field>
          </div>
          <Field label="Tooltip body">
            <TextArea
              value={b.tipBody ?? ""}
              onChange={(e) => setBubble(i, { tipBody: e.target.value })}
              rows={3}
            />
          </Field>
          <Field label="Subtitle (optional)">
            <TextInput
              value={b.subtitle ?? ""}
              onChange={(e) => setBubble(i, { subtitle: e.target.value })}
            />
          </Field>
        </div>
      ))}
    </SectionFrame>
  );
}
